/**
 * Dashboard shell: gates the workspace behind a live session and
 * renders the persistent sidebar / header chrome around every nested
 * route.
 *
 * Equivalent of the old Next.js ``app/(dashboard)/layout.tsx``. The
 * loader is what enforces auth: it calls `requireAuth`, which throws
 * a redirect to /login on 401. The header workspace switcher is
 * loaded best-effort alongside identity so a flaky `/v1/me/teams`
 * doesn't block the entire dashboard.
 */

import { type LoaderFunctionArgs, Outlet, useLoaderData, useNavigate } from "react-router-dom";

import { AppHeader } from "@/components/app-header";
import { AppSidebar } from "@/components/app-sidebar";
import { HeaderUserMenu } from "@/components/header-user-menu";
import { TeamSwitcher, type TeamSwitcherTeam } from "@/components/team-switcher";
import { TopProgress } from "@/components/top-progress";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";

import { AuthProvider, type AuthState } from "../lib/auth";
import { invalidateAuthCaches, loadTeams, requireAuth } from "../lib/loaders";

interface DashboardLoaderData {
  auth: AuthState;
  teams: TeamSwitcherTeam[];
}

export async function dashboardLoader({
  request,
}: LoaderFunctionArgs): Promise<DashboardLoaderData> {
  // ``requireAuth`` and ``loadTeams`` are both stale-while-revalidate
  // backed by sessionStorage. On a refresh both return cached values
  // immediately; on a fresh visit they run in parallel.
  const [auth, teams] = await Promise.all([requireAuth(request), loadTeams()]);
  return { auth, teams };
}

export function DashboardShell() {
  const { auth, teams } = useLoaderData() as DashboardLoaderData;
  const navigate = useNavigate();
  const activeTeam = teams.find((t) => t.is_active);

  const signOutAction = async (): Promise<void> => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {
      // Cookie clear is best-effort; even on 5xx we still bounce the user
      // back to /login so the SPA state matches what they expect.
    }
    // Wipe the sessionStorage cache so the very next load doesn't show
    // the previous user's name/team flash before the redirect lands.
    invalidateAuthCaches();
    navigate("/login", { replace: true });
  };

  return (
    <AuthProvider state={auth}>
      <TopProgress />
      <div className="flex h-screen w-screen overflow-hidden bg-background">
        <AppSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <AppHeader>
            {activeTeam ? (
              <TeamSwitcher
                teams={teams}
                activeTeamId={activeTeam.team_id}
                activeTeamName={activeTeam.team_name}
              />
            ) : null}
            <HeaderUserMenu
              name={auth.user.name}
              email={auth.user.email}
              image={auth.user.image ?? null}
              signOutAction={signOutAction}
            />
          </AppHeader>
          <main className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-[1440px] px-4 py-4 lg:px-5">
              <Outlet />
            </div>
          </main>
        </div>
        <Toaster />
      </div>
    </AuthProvider>
  );
}
